import { ArrowDown, ArrowUp, Minus } from 'react-feather';
import { type IntlShape } from 'react-intl';
import {
  CalibrationComment,
  CalibrationCommentVisibilty,
  CalibrationRecommendation,
  ThreadedCalibrationComment,
} from '../types';

export const RECOMMENDATION_FILTER_ORDER: CalibrationRecommendation[] = [
  'R',
  'K',
  'L',
];

export const getRecommendationDetails = (
  formatMessage: IntlShape['formatMessage'],
  option?: CalibrationRecommendation
) => {
  switch (option) {
    case 'R':
      return {
        icon: <ArrowUp size={16} aria-hidden="true" />,
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommend_raise_label',
          defaultMessage: 'Recommend raise',
        }),
        tooltip: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommendation.tooltip.raise',
          defaultMessage:
            "Suggest increasing this participant's rating during calibration.",
        }),
      };
    case 'K':
      return {
        icon: <Minus size={16} aria-hidden="true" />,
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommend_keep_label',
          defaultMessage: 'Recommend keep',
        }),
        tooltip: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommendation.tooltip.keep',
          defaultMessage:
            "Indicate the participant's rating should remain as-is.",
        }),
      };
    case 'L':
      return {
        icon: <ArrowDown size={16} aria-hidden="true" />,
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommend_lower_label',
          defaultMessage: 'Recommend lower',
        }),
        tooltip: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommendation.tooltip.lower',
          defaultMessage:
            "Suggest decreasing this participant's rating during calibration.",
        }),
      };
    default:
      return {
        icon: null,
        label: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommendation.none',
          defaultMessage: 'No recommendation',
        }),
        tooltip: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommendation.tooltip.none',
          defaultMessage: 'Share context without recommending a rating change.',
        }),
      };
  }
};

export const flattenThreadedComments = (
  comments: ThreadedCalibrationComment[]
): ThreadedCalibrationComment[] => {
  const flattened: ThreadedCalibrationComment[] = [];
  const flatten = (comment: ThreadedCalibrationComment) => {
    flattened.push(comment);
    comment.replies.forEach(flatten);
  };
  comments.forEach(flatten);
  return flattened;
};

export const isCalibrationRecommendation = (
  value: any
): CalibrationRecommendation | undefined =>
  typeof value === 'string' &&
  RECOMMENDATION_FILTER_ORDER.includes(value as CalibrationRecommendation)
    ? (value as CalibrationRecommendation)
    : undefined;

export const groupCommentsByThread = (
  comments: CalibrationComment[],
  showPrivateComments: boolean
) => {
  // Sort all comments by creation date and convert to ThreadedCalibrationComment
  const sortedComments: ThreadedCalibrationComment[] = comments
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
    .map((c) => ({
      ...c,
      thread_id: 0,
      replies: [],
    }));

  // Create a map to hold threaded comments by ID
  const commentMap: { [key: string | number]: ThreadedCalibrationComment } = {};

  // First pass: create ThreadedCalibrationComment objects
  sortedComments.forEach((comment) => {
    commentMap[comment.id] = comment;
  });

  // Second pass: build the tree structure
  const rootComments: ThreadedCalibrationComment[] = [];

  sortedComments.forEach((comment) => {
    const threadedComment = commentMap[comment.id]!;

    if (comment.parent_comment_id) {
      // This is a reply, add it to parent's replies
      const parent = commentMap[comment.parent_comment_id];
      if (parent) {
        // set the thread id to the current thread id
        threadedComment.thread_id = parent.thread_id;
        parent.replies.push(threadedComment);
      }
    } else {
      // This is a root comment
      // for the root, the thread id is the root itself
      threadedComment.thread_id = threadedComment.id;
      rootComments.push(threadedComment);
    }
  });

  // Filter out private comments and their replies if showPrivateComments is false
  const filterPrivateComments = (
    comments: ThreadedCalibrationComment[]
  ): ThreadedCalibrationComment[] => {
    if (showPrivateComments) {
      return comments;
    }

    return comments
      .filter(
        (comment) =>
          comment.visibility ===
          CalibrationCommentVisibilty.AUTHOR_AND_REVIEWERS
      )
      .map((comment) => ({
        ...comment,
        replies: filterPrivateComments(comment.replies),
      }));
  };

  const filteredRootComments = filterPrivateComments(rootComments);
  return filteredRootComments;
};
